import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface TableActionMenuItem {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  tone?: 'default' | 'success' | 'danger';
  dividerBefore?: boolean;
}

interface TableActionMenuProps {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  items: TableActionMenuItem[];
  ariaLabel?: string;
}

/** Menu ba chấm dùng chung cho action trong table, bắt nguồn từ Trung tâm thanh toán. */
export function TableActionMenu({ open, onToggle, onClose, items, ariaLabel = 'Thao tác' }: TableActionMenuProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<{ top: number; right: number; openUp: boolean } | null>(null);

  useEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const openUp = window.innerHeight - rect.bottom < 220;
    setPosition({ top: openUp ? rect.top - 4 : rect.bottom + 4, right: window.innerWidth - rect.right, openUp });
    const closeOnScroll = () => onClose();
    window.addEventListener('scroll', closeOnScroll, true);
    return () => window.removeEventListener('scroll', closeOnScroll, true);
  }, [open, onClose]);

  const run = (item: TableActionMenuItem) => () => { onClose(); item.onClick(); };

  return (
    <div className="table-action-menu-wrap">
      <button ref={buttonRef} type="button" className="table-action-menu-trigger" title={ariaLabel} aria-label={ariaLabel} aria-expanded={open} onClick={onToggle}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.55" /><circle cx="12" cy="12" r="1.55" /><circle cx="12" cy="19" r="1.55" /></svg>
      </button>
      {open && position && createPortal(
        <>
          <div className="table-action-menu-backdrop" onClick={onClose} />
          <div className="table-action-menu-pop" style={{ position: 'fixed', top: position.openUp ? undefined : position.top, bottom: position.openUp ? window.innerHeight - position.top : undefined, right: position.right }}>
            {items.map((item, index) => (
              <div key={`${item.label}-${index}`}>
                {item.dividerBefore && <div className="table-action-menu-separator" />}
                <button type="button" className={`table-action-menu-item ${item.tone === 'success' ? 'is-success' : ''} ${item.tone === 'danger' ? 'is-danger' : ''}`} onClick={run(item)}>
                  {item.icon && <span className="table-action-menu-icon">{item.icon}</span>}
                  {item.label}
                </button>
              </div>
            ))}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}
